import { REWRITE_TIERS, type RewriteTier } from '@lyrmo/shared';
import { useState } from 'react';
import { Button } from '../../components/Button';
import type { CopyStatus } from '../../hooks/useClipboard';
import { useI18n } from '../../i18n/i18n-context';
import { downloadText, textFileName } from '../../lib/download';
import { EmptyState } from './EmptyState';
import { RateLimitNotice } from './RateLimitNotice';
import { allVersionsText, rewriteAnnouncement } from './results-text';
import { ResultCard, type CardResult } from './ResultCard';
import { RewriteSign } from './RewriteSign';
import type { RewriteState } from './useRewrite';
import type { Suggest } from './useAlternatives';
import type { Explain } from './useExplanations';
import { CreditLimitNotice } from './CreditLimitNotice';
import type { Speech } from './useSpeech';
import './results-panel.css';

interface ResultsPanelProps {
  readonly state: RewriteState;
  readonly copied: CopyStatus<RewriteTier> | null;
  readonly cooldownSeconds: number;
  readonly onCopy: (index: number) => void;
  readonly onRetry: () => void;
  /** Makes the version at `index` the draft, to edit it or rewrite it again. */
  readonly onEdit: (index: number) => void;
  /** Whether a version can be made the draft now; true unless said otherwise. */
  readonly editable?: boolean;
  /** Offered when the daily credit limit is reached. */
  readonly onSeePlans?: () => void;
  /** Explains a version's changes, in its Why view. */
  readonly onExplain?: Explain;
  /** Suggests other ways to say a version's changes, in its Alts view, with onRevise. */
  readonly onSuggest?: Suggest | undefined;
  /** Clears the versions, leaving the draft as it is. */
  readonly onClear?: (() => void) | undefined;
  /** Makes `text` the version of `tier` from request `id`, once other words are swapped in. */
  readonly onRevise?: ((id: number, tier: RewriteTier, text: string) => void) | undefined;
  /** Reads versions aloud, one voice shared with the draft's; omitted where there's no voice. */
  readonly speech?: Speech | undefined;
  /** Desktop hosts save through a system dialog and can insert a version into another app. */
  readonly onSaveText?: ((name: string, text: string) => void) | undefined;
  readonly onUse?: ((index: number) => void) | undefined;
  /** Shows the host's insert action as a labeled button below each version. */
  readonly useLabel?: string | undefined;
  readonly usable?: boolean | undefined;
  readonly retryable?: boolean | undefined;
  /** Beside the heading once the versions are ready: a host's shortcut to insert one, say. */
  readonly shortcutHint?: string | undefined;
}

type ActiveState = Exclude<RewriteState, { status: 'idle' }>;

function cardResult(state: ActiveState, tier: RewriteTier): CardResult {
  switch (state.status) {
    case 'loading':
      return { status: 'loading' };
    case 'error':
      return { status: 'error' };
    case 'success': {
      const text = state.rewrites[tier];
      // A blank tier fails on its own card; the others are still useful.
      return text
        ? { status: 'ready', text, original: state.text, returned: state.returned?.[tier] ?? text }
        : { status: 'error' };
    }
  }
}

export function ResultsPanel({
  state,
  copied,
  cooldownSeconds,
  onCopy,
  onRetry,
  onEdit,
  editable = true,
  onSeePlans,
  onExplain,
  onSuggest,
  onRevise,
  onClear,
  speech,
  onSaveText = downloadText,
  onUse,
  useLabel,
  usable = true,
  retryable = true,
  shortcutHint,
}: ResultsPanelProps) {
  const i18n = useI18n();
  const { t } = i18n;
  // The request whose cards are showing, and whether they took the place of the last one's, on the
  // page before them: those are there already, and don't come in again.
  const cardsFor =
    state.status === 'idle' ||
    (state.status === 'error' &&
      (state.error.code === 'RATE_LIMITED' || state.error.code === 'CREDIT_LIMIT_REACHED'))
      ? null
      : state.id;
  const [cards, setCards] = useState({ id: cardsFor, replacing: false });
  if (cards.id !== cardsFor)
    setCards({ id: cardsFor, replacing: cardsFor !== null && cards.id !== null });
  let content;
  if (state.status === 'idle') {
    content = <EmptyState />;
  } else if (state.status === 'error' && state.error.code === 'RATE_LIMITED') {
    content = <RateLimitNotice secondsLeft={cooldownSeconds} />;
  } else if (state.status === 'error' && state.error.code === 'CREDIT_LIMIT_REACHED') {
    content = <CreditLimitNotice error={state.error} onSeePlans={onSeePlans} />;
  } else {
    content = REWRITE_TIERS.map((tier, index) => (
      // Keyed by request so each new rewrite starts with fresh cards.
      <ResultCard
        key={`${state.id}:${tier}`}
        tier={tier}
        index={index}
        result={cardResult(state, tier)}
        copyOutcome={copied?.key === tier ? copied.outcome : undefined}
        onCopy={() => {
          onCopy(index);
        }}
        onRetry={onRetry}
        onEdit={() => {
          onEdit(index);
        }}
        editable={editable}
        replacing={cards.replacing}
        onUse={
          onUse
            ? () => {
                onUse(index);
              }
            : undefined
        }
        useLabel={useLabel}
        usable={usable}
        retryable={retryable}
        onSaveText={onSaveText}
        onExplain={onExplain}
        onSuggest={onSuggest}
        onRevise={
          onRevise
            ? (text) => {
                onRevise(state.id, tier, text);
              }
            : undefined
        }
        speech={speech?.state?.source === tier ? speech.state : null}
        onSpeak={
          speech
            ? () => {
                if (state.status === 'success') void speech.speak(tier, state.rewrites[tier]);
              }
            : undefined
        }
        onStopSpeech={speech?.stop}
      />
    ));
  }

  // Without words while the versions are written, words juggled; nothing once they're ready,
  // unless the host has a hint.
  const meta =
    state.status === 'loading' ? (
      <RewriteSign />
    ) : state.status === 'success' ? (
      shortcutHint
    ) : undefined;

  return (
    <section
      className="results-panel"
      aria-labelledby="results-heading"
      aria-busy={state.status === 'loading'}
    >
      <div className="section-header">
        <h2 className="section-title" id="results-heading">
          {t.results.heading}
        </h2>
        <div className="section-actions">
          {meta && (
            <p key={state.status} className="section-meta" aria-hidden="true">
              {meta}
            </p>
          )}
          {state.status === 'success' && (
            <Button
              icon="download"
              title={t.results.downloadAllTitle}
              onClick={() => {
                onSaveText(textFileName(t.results.fileName), allVersionsText(state, t));
              }}
            >
              {t.results.downloadAll}
            </Button>
          )}
          {onClear && (state.status === 'success' || state.status === 'error') && (
            <Button
              icon="close"
              aria-label={t.results.clearTitle}
              title={t.results.clearTitle}
              onClick={onClear}
            >
              {t.results.clear}
            </Button>
          )}
        </div>
      </div>
      <div className="results-content">{content}</div>
      <p className="sr-only" role="status">
        {rewriteAnnouncement(state, i18n)}
      </p>
    </section>
  );
}
